import Link from "next/link";

import { BRAND_PATHS } from "@chef/constants";
import { Button, Card } from "@chef/components";
import { ClapIcon } from "@chef/icons/small";
import { ITellAFriendCompetition } from "@chef/feature-sanity";

import { CompetitionInvitationNotification } from "./TellAFriendCompetition/CompetitionInvitationNotification";
import { useCompetitionDateValidationData } from "../../hooks/useCompetitionDateValidationData";

import { intl } from "./TellAFriendConfirmation.Intl";

interface TellAFriendConfirmationProps {
  openDisclosure?: boolean;
  competitionContent: ITellAFriendCompetition;
  onScrollToCompetitionInformationClicked: () => void;
}

interface ConfirmationNotificationLayoutProps {
  children: React.ReactNode;
  hasCompetitionInformationLink?: boolean;
  ctaText?: string;
  onScrollToCompetitionInformationClicked?: () => void;
}

export const InvitationSentNotification = () => {
  return (
    <div className="md:mx-16">
      <div className="flex items-center justify-center w-20 h-20 mx-auto mt-8 rounded-full bg-grey-3">
        <ClapIcon width={60} height={60} />
      </div>

      <h1 className="mt-6 mb-2 text-lg md:text-xl">
        <strong>{intl.INVITAION_SENT}</strong>
      </h1>

      <div>{intl.YOU_KNOW_THAT_YOU_GET_A_DISCOUNT_PER_FRIEND}</div>
    </div>
  );
};

export const ConfirmationNotificationLayout = ({
  children,
  ctaText,
  hasCompetitionInformationLink = false,
  onScrollToCompetitionInformationClicked,
}: ConfirmationNotificationLayoutProps) => {
  return (
    <Card className="text-center">
      {children}
      <Link href={BRAND_PATHS.TELL_A_FRIEND_HREF} passHref legacyBehavior>
        <Button primary full className="my-6 md:w-78">
          {hasCompetitionInformationLink
            ? ctaText
            : intl.COLLECT_MORE_DISCOUNTS}
        </Button>
      </Link>
      {hasCompetitionInformationLink && (
        <button
          onClick={onScrollToCompetitionInformationClicked}
          className="text-sm underline text-primary"
        >
          <strong>{intl.READ_MORE_ABOUT_THIS_MONTHS_COMPETITION}</strong>
        </button>
      )}
    </Card>
  );
};

export const TellAFriendConfirmation = ({
  competitionContent,
  onScrollToCompetitionInformationClicked,
}: TellAFriendConfirmationProps) => {
  const competitionSetting = competitionContent?.competitionSetting;

  const competitionRequirements = competitionSetting?.competitionRequirements;

  const dateSetting = competitionSetting?.dateSetting;

  const { invitesRequired, deliveriesRequired } = competitionRequirements;

  const { deliveryCutoffDate, endDate, startDate } = dateSetting;

  const { isCompetitionPeriod } = useCompetitionDateValidationData({
    startDate: startDate,
    endDate: endDate,
    deliveryCutoffDate: deliveryCutoffDate,
  });

  // We switch over to the default instant confirmation when the competition ends.
  if (isCompetitionPeriod) {
    return (
      <ConfirmationNotificationLayout
        onScrollToCompetitionInformationClicked={
          onScrollToCompetitionInformationClicked
        }
        hasCompetitionInformationLink
        ctaText={
          isCompetitionPeriod
            ? intl.INVITE_MORE_FRIENDS
            : intl.COLLECT_MORE_DISCOUNTS
        }
      >
        <CompetitionInvitationNotification
          invitesRequired={invitesRequired}
          deliveriesRequired={deliveriesRequired}
          startDate={startDate}
          endDate={endDate}
          deliveryCutoffDate={deliveryCutoffDate}
        />
      </ConfirmationNotificationLayout>
    );
  }

  return (
    <ConfirmationNotificationLayout>
      <InvitationSentNotification />
    </ConfirmationNotificationLayout>
  );
};
